import { useEffect } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import Loading from '@components/UI/Loading'
import { useModal } from '@hooks/useModal'
import { selectCampaign } from '@store/campaigns'
import { IApplicationState } from '@store/index'
import {
  selectIsPitchComparisonFetching,
  selectPitchComparisonError,
  selectPitchSubmissionComparisonResults,
} from '@store/submissions'
import { fetchPitchComparison } from '@store/submissions/actions/compareSubmission'
import Theme from '@theme'
import { CloseIcon, Modal } from '@tribegroup'
import { CompareSubmissionPreview } from './CompareSubmissionPreview'
import { PreviewContainer } from './PitchComparisonModal.styled'

type PitchComparisonModalProps = {
  submissionId: number
  campaignId: number
}

export const PitchComparisonModal: React.FC<PitchComparisonModalProps> = ({
  submissionId,
  campaignId,
}) => {
  const { isOpen, hideModal } = useModal()
  const dispatch = useDispatch()
  const { isFetching, error, comparison, campaign } = useSelector((state: IApplicationState) => ({
    isFetching: selectIsPitchComparisonFetching(state, submissionId),
    error: selectPitchComparisonError(state, submissionId),
    comparison: selectPitchSubmissionComparisonResults(state, submissionId),
    campaign: selectCampaign(state, campaignId),
  }), shallowEqual)

  const isPitchingEnabled = campaign?.pitching_enabled

  useEffect(() => {
    dispatch(fetchPitchComparison(submissionId))
  }, [])

  // Determine labels based on pitching_enabled
  const firstVersionHeaderKey = isPitchingEnabled
    ? 'submission.card.action.pitchComparison.modal.pitch'
    : 'submission.card.action.pitchComparison.modal.initialSubmission'

  const firstVersionTimestampKey = isPitchingEnabled
    ? 'submission.card.action.pitchComparison.modal.timestamp.pitch'
    : 'submission.card.action.pitchComparison.modal.timestamp.initialSubmission'

  return (
    <Modal
      reactModalProps={{
        isOpen: Boolean(isOpen),
      }}
      width={55}
      padding={3.5}
      mobilePadding={1}
      contentBackgroundColor={Theme.grey100Color}
    >
      <CloseIcon size={1.5} top={1} right={1} onClick={hideModal} />
      {isFetching && <Loading testId="loading" />}
      {error && <PreviewContainer fullWidth>{error.message}</PreviewContainer>}
      {comparison && (
        <PreviewContainer fullWidth>
          <CompareSubmissionPreview
            headerMessageId={firstVersionHeaderKey}
            submissionId={submissionId}
            campaignId={campaignId}
            version={comparison.pitch_version}
            timestampMessageId={firstVersionTimestampKey}
          />
          <CompareSubmissionPreview
            headerMessageId="submission.card.action.pitchComparison.modal.finalSubmission"
            submissionId={submissionId}
            campaignId={campaignId}
            version={comparison.current_version}
            timestampMessageId="submission.card.action.pitchComparison.modal.timestamp.finalSubmission"
          />
        </PreviewContainer>
      )}
    </Modal>
  )
}
